import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatRelative } from '@/lib/format';
import { useRealtime } from '@/lib/socket';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadCount,
} from '@/features/notifications/api';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';

const SEVERITY_DOT: Record<string, string> = {
  INFO: 'bg-info',
  WARNING: 'bg-warning',
  CRITICAL: 'bg-danger',
};

export function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const { data: unread } = useUnreadCount();
  const { data, isLoading, isError } = useNotifications({ page: 1, pageSize: 12 });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  // Atualiza a lista assim que uma notificação chega pelo WebSocket.
  useRealtime({
    onNotification: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const count = unread?.count ?? 0;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={count > 0 ? `Notificações (${count} não lidas)` : 'Notificações'}
        onClick={() => setOpen((current) => !current)}
        className="relative rounded-lg p-2 text-muted transition hover:bg-elevated hover:text-fg"
      >
        <Bell className="h-5 w-5" aria-hidden />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Notificações"
          className="absolute right-0 z-40 mt-1 w-[min(24rem,92vw)] overflow-hidden rounded-lg border border-line bg-surface shadow-pop"
        >
          <header className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <h2 className="text-sm font-semibold text-fg">Notificações</h2>
            {count > 0 && (
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<CheckCheck className="h-3.5 w-3.5" />}
                loading={markAllRead.isPending}
                onClick={() => markAllRead.mutate()}
              >
                Marcar todas
              </Button>
            )}
          </header>

          <div className="max-h-[26rem] overflow-y-auto">
            {isLoading && (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            )}

            {isError && (
              <p className="px-4 py-6 text-center text-sm text-danger">
                Não foi possível carregar as notificações.
              </p>
            )}

            {!isLoading && !isError && (data?.items.length ?? 0) === 0 && (
              <EmptyState compact title="Tudo em dia" description="Nenhuma notificação por aqui." />
            )}

            <ul>
              {data?.items.map((notification) => (
                <li key={notification.id} className="border-b border-line/70 last:border-0">
                  <div
                    className={cn(
                      'flex gap-3 px-4 py-3 transition hover:bg-elevated',
                      !notification.readAt && 'bg-primary-soft/30',
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                        SEVERITY_DOT[notification.severity] ?? 'bg-muted',
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-fg">{notification.title}</p>
                      <p className="mt-0.5 text-xs text-muted">{notification.message}</p>
                      <div className="mt-1.5 flex items-center gap-3 text-xs">
                        <span className="text-muted">{formatRelative(notification.createdAt)}</span>
                        {notification.link && (
                          <Link
                            to={notification.link}
                            onClick={() => setOpen(false)}
                            className="font-medium text-primary hover:underline"
                          >
                            Abrir
                          </Link>
                        )}
                        {!notification.readAt && (
                          <button
                            type="button"
                            onClick={() => markRead.mutate(notification.id)}
                            className="text-muted hover:text-fg hover:underline"
                          >
                            Marcar como lida
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
